import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Pagination,
  PaginationItem,
  PaginationLink,
} from 'reactstrap';
import { FaNewspaper, FaPlus, FaEdit, FaTrash, FaEye, FaBullhorn } from 'react-icons/fa';
import API from '../utils/api';

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(20);
  const [totalNews, setTotalNews] = useState(0);

  // Modal state
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    is_published: false
  });

  useEffect(() => {
    loadNews();
  }, [currentPage]);

  const loadNews = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({
        page: currentPage,
        per_page: perPage,
        include_unpublished: 'true' // Admin can see all
      });

      const response = await API.get(`/news?${params.toString()}`);
      setNews(response.data.news);
      setTotalPages(response.data.pagination.pages);
      setTotalNews(response.data.pagination.total);
    } catch (err) {
      setError('Failed to load news articles');
      console.error('Load news error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      title: '',
      content: '',
      summary: '',
      is_published: false
    });
    setEditMode(false);
    setSelectedNews(null);
    setModal(true);
  };

  const handleEdit = (newsItem) => {
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      summary: newsItem.summary || '',
      is_published: newsItem.is_published
    });
    setEditMode(true);
    setSelectedNews(newsItem);
    setModal(true);
  };

  const handleDelete = (newsItem) => {
    setNewsToDelete(newsItem);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await API.delete(`/news/${newsToDelete.id}`);
      setSuccess('News article deleted successfully');
      setDeleteModal(false);
      setNewsToDelete(null);
      loadNews();
    } catch (err) {
      setError('Failed to delete news article');
      console.error('Delete error:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');

      if (editMode) {
        await API.put(`/news/${selectedNews.id}`, formData);
        setSuccess('News article updated successfully');
      } else {
        await API.post('/news', formData);
        setSuccess('News article created successfully');
      }

      setModal(false);
      loadNews();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save news article');
      console.error('Save error:', err);
    }
  };

  const togglePublish = async (newsItem) => {
    try {
      await API.put(`/news/${newsItem.id}/publish`, {
        is_published: !newsItem.is_published
      });
      setSuccess(`News article ${!newsItem.is_published ? 'published' : 'unpublished'} successfully`);
      loadNews();
    } catch (err) {
      setError('Failed to update publish status');
      console.error('Publish toggle error:', err);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : '-';
  };

  const getStatusBadge = (newsItem) => {
    if (newsItem.is_published) {
      return <Badge className="bg-dark text-success border border-success">Published</Badge>;
    }
    return <Badge className="bg-dark text-warning border border-warning">Draft</Badge>;
  };

  return (
    <Container fluid>
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-heading">
                <FaNewspaper className="page-heading-icon" />
                News Management
              </h2>
              <small className="text-muted">Create and manage news announcements</small>
            </div>
            <Button color="primary" onClick={handleCreate}>
              <FaPlus className="me-1" />
              Create News
            </Button>
          </div>
        </Col>
      </Row>

      {error && <Alert color="danger" dismissible onDismiss={() => setError('')}>{error}</Alert>}
      {success && <Alert color="success" dismissible onDismiss={() => setSuccess('')}>{success}</Alert>}

      {/* Stats */}
      <Row className="mb-4">
        <Col md="3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">Total Articles</CardTitle>
              <h3 className="mb-0">{totalNews}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">Published</CardTitle>
              <h3 className="mb-0 text-success">
                {news.filter(n => n.is_published).length}
              </h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card>
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-1">Drafts</CardTitle>
              <h3 className="mb-0 text-warning">
                {news.filter(n => !n.is_published).length}
              </h3>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* News Table */}
      <Card>
        <CardBody>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : news.length > 0 ? (
            <>
              <div className="table-responsive">
                <Table striped>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Published</th>
                      <th>Author</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {news.map((newsItem) => (
                      <tr key={newsItem.id}>
                        <td>
                          <strong>{newsItem.title}</strong>
                          {newsItem.summary && (
                            <div className="text-muted small">{newsItem.summary}</div>
                          )}
                        </td>
                        <td>{getStatusBadge(newsItem)}</td>
                        <td>{formatDate(newsItem.created_at)}</td>
                        <td>{formatDate(newsItem.published_at)}</td>
                        <td>{newsItem.created_by || '-'}</td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <Button
                              color="outline-info"
                              size="sm"
                              onClick={() => handleEdit(newsItem)}
                              title="Edit"
                            >
                              <FaEdit size={14} />
                            </Button>
                            <Button
                              color={newsItem.is_published ? "outline-warning" : "outline-success"}
                              size="sm"
                              onClick={() => togglePublish(newsItem)}
                              title={newsItem.is_published ? "Unpublish" : "Publish"}
                            >
                              <FaBullhorn size={14} />
                            </Button>
                            <Button
                              color="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(newsItem)}
                              title="Delete"
                            >
                              <FaTrash size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <PaginationItem disabled={currentPage <= 1}>
                      <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                    </PaginationItem>
                    {[...Array(totalPages)].map((_, i) => (
                      <PaginationItem key={i} active={currentPage === i + 1}>
                        <PaginationLink onClick={() => handlePageChange(i + 1)}>
                          {i + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem disabled={currentPage >= totalPages}>
                      <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                    </PaginationItem>
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-5">
              <h5 className="text-muted">No news articles found</h5>
              <p className="text-muted">Create your first news article to get started.</p>
              <Button color="primary" onClick={handleCreate}>
                <FaPlus className="me-1" />
                Create News Article
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={modal} toggle={() => setModal(!modal)} size="lg">
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={() => setModal(!modal)}>
            {editMode ? 'Edit News Article' : 'Create News Article'}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="title">Title *</Label>
              <Input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="bg-secondary border-secondary text-white"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label for="summary">Summary</Label>
              <Input
                type="textarea"
                id="summary"
                rows="2"
                value={formData.summary}
                onChange={(e) => setFormData({...formData, summary: e.target.value})}
                className="bg-secondary border-secondary text-white"
                placeholder="Brief summary for notifications (optional)"
              />
            </FormGroup>

            <FormGroup>
              <Label for="content">Content *</Label>
              <Input
                type="textarea"
                id="content"
                rows="8"
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                className="bg-secondary border-secondary text-white"
                required
              />
            </FormGroup>

            <FormGroup check>
              <Input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
              />
              <Label check for="is_published">
                Publish immediately (will notify all users)
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" color="primary">
              {editMode ? 'Update' : 'Create'}
            </Button>
            <Button type="button" color="secondary" onClick={() => setModal(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)}>
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete the news article "{newsToDelete?.title}"?
          This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>
            Delete
          </Button>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default News;